import { Factory } from '../../services/Factory';
import { TrackService } from '../../services/auditing/TrackService';

import IScope = angular.IScope;
import IAugmentedJQuery = angular.IAugmentedJQuery;
import IDirective = angular.IDirective;

interface ITimelineScope extends IScope {
}

export class Timeline implements IDirective {
  public restrict: string = "E";
  public link: (scope: ITimelineScope, element: IAugmentedJQuery) => void;
  public template: string;
  public scope = {chart: "=ngModel", timezone: "=timezone"};
  public require = ["ngModel"];

  constructor(trackService: TrackService) {
    this.template = `
<div>
  <div class="timeline"></div>
  <div class="brush"></div>
</div>
`;

    this.link = (scope: ITimelineScope, element: IAugmentedJQuery) => {
        const dates = scope.chart.resourceCounts.map(r => moment(r.key).tz(scope.timezone));
        const counts = scope.chart.resourceCounts.map(r => r.value);

        scope.chart.startDate = _.min(dates);
        scope.chart.endDate = _.max(dates);

        const months = Timeline.GetTickValues(scope.chart.startDate, scope.chart.endDate);

        const chart = c3.generate({
          bindto: element.find(".timeline")[0],
          size: {
            height: 460
          },
          data: {
            x: "x",
            columns: [
              ["x", ...dates],
              ["Document Count", ...counts]
            ],
            types: {
              "Document Count": "area"
            },
            onclick: e => {
              scope.$emit("chartItemSelected", element, {min: e.x, max: e.x});
            }
          },
          padding: {
            left: 20,
            right: 20
          },
          transition: {
            duration: scope.animation === false ? 0 : 750
          },
          legend: {
            show: false
          },
          tooltip: {
            format: {
              title: (x, index) => d3.timeFormat("%B %d, %Y")(new Date(x as number))
            }
          },
          axis: {
            x: {
              localtime: false,
              type: "timeseries",
              tick: {
                format: "%b %Y",
                values: months
              }
            },
            y: {
              inner: true,
              tick: {
                format: v => d3.format(",d")(v)
              }
            }
          }
        });
        const brush = c3.generate({
          bindto: element.find(".brush")[0],
          size: {
            height: 50
          },
          data: {
            x: "x",
            columns: [
              ["x", ...dates],
              ["Document Count", ...counts]
            ],
            types: {
              "Document Count": "bar"
            }
          },
          padding: {
            right: 20
          },
          tooltip: {
            show: false
          },
          zoom: {
            enabled: true,
            type: "drag",
            disableDefaultBehavior: true,
            onzoomstart: () => {
              scope.brushing = false;

              setTimeout(() => {
                if (!scope.brushing) {
                  chart.internal.config.axis_x_tick_values = months;
                  chart.unzoom();

                  trackService.TrackEvent("chart-timeline", "chart-timeline-zoom-out");
                } else {
                  scope.brushing = false;
                }
              }, 500);
            },
            onzoom: () => {
              scope.brushing = true;
            },
            onzoomend: domain => {
              scope.chart.startDate = new Date(domain[0]);
              scope.chart.endDate = new Date(domain[1]);

              chart.internal.config.axis_x_tick_values = Timeline.GetTickValues(scope.chart.startDate, scope.chart.endDate);
              chart.zoom(domain);
              brush.unzoom();
              d3.select(".c3-drag-zoom").classed('disabled', false);
              trackService.TrackEvent("chart-timeline", "chart-timeline-zoom-in");
            }
          },
          transition: {
            duration: scope.animation === false ? 0 : 750
          },
          legend: {
            show: false
          },
          axis: {
            x: {
              localtime: false,
              type: "timeseries",
              tick: {
                format: "%Y",
                count: months.length,
                culling: {
                  max: 14
                }
              }
            },
            y: {
              min: _.min(counts),
              max: _.min(counts)
            }
          }
        });
    };
  }

  private static GetTickValues(begin: Date, end: Date) {
      const beginDate = moment(begin);
      const endDate = moment(end);
      const months = [];
      const culling = 20;

      while (beginDate < endDate) {
        months.push(beginDate.startOf("month").toDate());
        beginDate.add(1, "month");
      }

      const skipInterval = Math.min(months.length, Math.ceil(months.length / culling));

      for (let i = 1; i < months.length; i += skipInterval) {
        for (let j = i; j < i + skipInterval - 1; j++) {
          months[j] = null;
        }
      }

    return months;
  }
}

frontendApp.directive("iioTimeLineChart", ["TrackService", Factory.Create(Timeline)]);
